import React from 'react';
import styled from 'styled-components';
import { PrimaryButton, SecondaryButton } from './components';

/**
 * FormShell is the chrome around every step of a flow: title, progress bar,
 * body slot, and a footer with primary/secondary actions.
 *
 * The footer buttons use the shared button variants so they automatically
 * pick up the tenant palette injected through `ThemeProvider`.
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ theme }) => String(theme.colors.general_background ?? '#FFFFFF')};
  color: ${({ theme }) => String(theme.colors.general_font_primary)};
`;

const Header = styled.header`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => String(theme.colors.general_dividers ?? '#DADADA')};
`;

const Title = styled.h1`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 600;
`;

const Progress = styled.div`
  display: flex;
  gap: 4px;
`;

const Dot = styled.span<{ $active: boolean }>`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${({ $active, theme }) =>
    $active
      ? String(theme.colors.progress_and_loading_main ?? theme.colors.standard_button_primary_default_background ?? '#00CECE')
      : String(theme.colors.progress_and_loading_background ?? theme.colors.general_dividers ?? '#F4F4F4')};
  transition: background 200ms ease;
`;

const Body = styled.main`
  flex: 1;
  padding: 24px;
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => String(theme.colors.general_dividers ?? '#DADADA')};
  background: ${({ theme }) => String(theme.colors.forms_default_background ?? '#F2F5F7')};
`;

type Props = {
  title: string;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  nextLabel?: string;
  backLabel?: string;
  isSubmitting?: boolean;
  children: React.ReactNode;
};

export const FormShell: React.FC<Props> = ({
  title,
  stepIndex,
  totalSteps,
  onNext,
  onBack,
  nextLabel = 'Next',
  backLabel = 'Back',
  isSubmitting = false,
  children,
}) => (
  <Container>
    <Header>
      <Title>{title}</Title>
      <Progress aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <Dot key={i} $active={i <= stepIndex} />
        ))}
      </Progress>
    </Header>
    <Body>{children}</Body>
    <Footer>
      <SecondaryButton onClick={onBack} disabled={isSubmitting}>
        {backLabel}
      </SecondaryButton>
      <PrimaryButton onClick={onNext} disabled={isSubmitting}>
        {nextLabel}
      </PrimaryButton>
    </Footer>
  </Container>
);
